<template>
  <GhPage class="tour-page-shell">
    <section class="section tour-page">
      <div class="container">
        <!-- Error State -->
        <div v-if="hasError" class="error-state p-6">
          <div class="has-text-centered">
            <h2 class="title is-4">{{ $t("tour.errorTitle") }}</h2>
            <p class="mb-4">{{ errorMessage }}</p>
            <button @click="retryLoading" class="button is-primary">{{ $t("tour.tryAgain") }}</button>
            <button
              @click="useFallbackData"
              class="button is-info ml-2"
              v-if="!tourData.title" >{{ $t("tour.useDemo") }}</button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-else-if="isLoading" class="loading-placeholder">
          <vue-lottie :options="lottieOptions" :width="180" height="100vh" />
          <p class="has-text-centered">{{ $t("tour.loading") }}</p>
        </div>

        <div v-else-if="!notFound">
          <div id="content" :class="{ 'has-overlay-back': isMobileView }">
            <GoBack class="back--btn" />

            <div class="columns is-desktop mt-5" :class="{ 'mt-5': !isMobileView }">
              <div
                class="column is-8"
                ref="description"
                :class="{ 'pr-6': !isMobileView }"
              >
                <div v-if="!isMobileView">
                  <b-skeleton height="282px" v-if="!isGalleryLoaded"></b-skeleton>
                  <ImageGallery
                    :images="gallery"
                    class="mb-5"
                    v-if="!isMobileView && isGalleryLoaded"
                  />
                </div>

                <div v-else class="carousel-skelton-mobile">
                  <b-skeleton height="282px" v-if="!isGalleryLoaded"></b-skeleton>
                  <b-carousel
                    :autoplay="false"
                    v-else
                    :class="{ 'mb-6': !isMobileView }"
                  >
                    <b-carousel-item v-for="(media, index) in gallery" :key="index">
                      <a class="image">
                        <img
                          v-if="media.type === 'image'"
                          :src="media.url"
                          :alt="`${tourData.title} - ${tourData.city} view ${
                            index + 1
                          }`"
                          loading="lazy"
                          class="rounded-image"
                        />
                        <b-loading v-else></b-loading>
                      </a>
                    </b-carousel-item>

                    <span
                      v-if="isGallery"
                      @click="switchGallery(true)"
                      class="modal-close is-large"
                    />
                  </b-carousel>
                </div>

                <div class="columns is-mobile mt-5">
                  <section class="column">
                    <h1 class="title mb-1 is-size-8-mobile">
                      {{ tourData.title }}
                    </h1>
                  </section>

                  <!-- <div
                    class="column has-text-right"
                    v-if="tourData.reviews && tourData.reviews.length"
                  >
                    <b-icon icon="star" class="mr-1 star-icon"></b-icon
                    ><span class="has-text-weight-bold">{{ overallRating }}</span>
                  </div> -->
                </div>
                <div class="is-flex">
                  <div
                    class="is-flex is-align-items-center activity--duration mb-4 mr-2"
                    v-if="tourData.city"
                  >
                    <img src="../assets/location-icon.svg" class="mr-1 icon" />
                    <span class="duration">{{ tourData.city }}</span>
                  </div>

                  <div
                    class="is-flex is-align-items-center activity--duration mb-4"
                    v-if="tourData.duration"
                  >
                    <img src="../assets/timer-icon.svg" class="mr-1 icon" />
                    <span class="duration">
                      {{ durationText(tourData.duration) }}</span
                    >
                  </div>
                </div>

                <div v-if="tourData.description" class="tour-description">
                  <p class="subtitle mb-4">{{ $t("tour.description") }}</p>
                  <p class="description">{{ showMore ? fullDescription : descriptionPreview }}</p>
                  <button
                    v-if="descriptionIsLong"
                    type="button"
                    class="more"
                    :aria-expanded="showMore ? 'true' : 'false'"
                    @click="toggleShowMore"
                  >
                    {{ showMore ? $t("tour.showLess") : $t("tour.showMore") }}
                  </button>
                </div>

                <div v-if="tourData.stops && tourData.stops.length > 1">
                  <p class="subtitle mt-4 mb-5">
                    {{ tourData.isPass ? $t("tour.route") : $t("tour.itinerary") }}
                  </p>

                  <div v-if="tourData.isPass">
                    <div
                      class="is-flex mb-5 scrollable-div"
                      v-if="tourData.scheduleInfo"
                    >
                      <div
                        class="route-info-container"
                        v-if="tourData.scheduleInfo.firstDeparture"
                      >
                        <p class="info-title">{{ $t("tour.firstDeparture") }}</p>
                        <p class="info-subtitle">
                          {{ tourData.scheduleInfo.firstDeparture }}
                        </p>
                      </div>

                      <div
                        class="route-info-container"
                        v-if="tourData.scheduleInfo.lastDeparture"
                      >
                        <p class="info-title">{{ $t("tour.lastDeparture") }}</p>
                        <p class="info-subtitle">
                          {{ tourData.scheduleInfo.lastDeparture }}
                        </p>
                      </div>

                      <div
                        class="route-info-container"
                        v-if="tourData.scheduleInfo.frequency"
                      >
                        <p class="info-title">{{ $t("tour.frequency") }}</p>
                        <p class="info-subtitle">
                          {{ tourData.scheduleInfo.frequency }}
                        </p>
                      </div>
                    </div>
                    <b-field class="is-flex mb-6">
                      <span
                        class="mr-2 display-flex is-align-items-center"
                        :class="{ 'has-text-weight-semibold': !isMapView }"
                        ><b-icon class="mr-2" icon="list-ul"></b-icon>{{ $t("tour.listView") }}
                      </span>
                      <b-switch v-model="isMapView" type="is-primary">
                        <span
                          :class="{ 'has-text-weight-semibold': isMapView }"
                          class="display-flex is-align-items-center"
                          ><b-icon class="mr-2 ml-1" icon="map" pack="far"></b-icon>{{ $t("tour.mapView") }}
                        </span>
                      </b-switch>
                    </b-field>

                    <RouteMapView
                      :locations="tourData.stops"
                      v-if="isMapView"
                      :class="{ 'mb-6 pb-5': tourData.isPass }"
                    />
                  </div>

                  <div
                    class="rightbox"
                    :class="{ 'mb-3': tourData.isPass }"
                    v-if="!isMapView"
                  >
                    <div class="rb-container">
                      <ul class="rb">
                        <li
                          class="rb-item"
                          v-for="(stop, index) in tourData.stops"
                          :key="index"
                        >
                          <div class="item-title mb-2">{{ stop.title }}</div>
                          <div
                            class="display-flex controls is-align-items-center mb-3 mt-3"
                            v-if="stop.canCall"
                          >
                            <b-tag type="is-success is-light p-4">
                              <section
                                class="display-flex is-align-items-center has-text-weight-semibold is-size-6"
                              >
                                <b-icon icon="thumbtack" class="mr-1"></b-icon>{{ $t("tour.pickupHere") }}
                              </section>
                            </b-tag>
                          </div>
                          <img
                            :src="stop.imageUrl"
                            v-if="stop.imageUrl"
                            class="mt-3 mb-3 stop--image"
                          />
                          <div class="item-subtitle mb-5" v-if="stop?.description">
                            {{ stop.description }}
                          </div>

                          <div v-if="tourData.isPass" class="is-flex mt-3 mb-5">
                            <a
                              target="_blank"
                              class="mr-5 is-flex is-align-items-center"
                              v-if="stop.direction"
                              :href="stop.direction"
                            >
                              <span class="grey-container mr-2">
                                <b-icon icon="compass" class="icon-sm"></b-icon>
                              </span>
                              <span class="link itenerary-options">{{ $t("tour.directions") }}</span>
                            </a>
                          </div>

                          <!-- Inline attractions display - always visible -->
                          <!--  <div
                            v-if="stop?.attractions?.length"
                            class="attractions-container mt-3"
                          >
                            <stop-attractions
                              :attractions="stop.attractions"
                              :stopName="stop.title"
                            />
                          </div> -->
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div class="columns is-multiline mb-5" v-if="tourData.isPass">
                  <div class="column is-6">
                    <div class="gray-container is-flex">
                      <img src="../assets/lightning.svg" class="mr-3 icon-md" />
                      <div>
                        <p class="has-text-weight-semibold">{{ $t("tour.pass.ownPaceTitle") }}</p>
                        <p class="has-text-weight-light">{{ $t("tour.pass.ownPaceText") }}</p>
                      </div>
                    </div>
                  </div>

                  <div class="column is-6">
                    <div class="gray-container is-flex">
                      <img src="../assets/calendar-tick.svg" class="mr-3 icon-md" />
                      <div>
                        <p class="has-text-weight-semibold">{{ $t("tour.pass.validityTitle") }}</p>
                        <p class="has-text-weight-light">{{ $t("tour.pass.validityText") }}</p>
                      </div>
                    </div>
                  </div>

                  <div
                    class="column is-6"
                    :class="{
                      'mt-4': !isMobileView,
                    }"
                  >
                    <div class="gray-container is-flex mb-3">
                      <img src="../assets/tag-user.svg" class="mr-3 icon-md" />
                      <div>
                        <p class="has-text-weight-semibold">{{ $t("tour.pass.guideTitle") }}</p>
                        <p class="has-text-weight-light">{{ $t("tour.pass.guideText") }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="columns mb-5">
                  <div class="column">
                    <h2 class="subtitle mb-0">{{ $t("tour.availableLanguages") }}</h2>
                  </div>
                  <div
                    class="column"
                    :class="{
                      'is-flex is-justify-content-end': !isMobileView,
                      'pb-4': isMobileView,
                    }"
                  >
                    <p class="description">{{ $t("tour.languagesList") }}</p>
                  </div>
                </div>

                <div class="columns mb-5">
                  <div class="column">
                    <h2 class="subtitle mb-0">{{ $t("tour.digitalTicket") }}</h2>
                  </div>
                  <div
                    class="column"
                    :class="{
                      'is-flex is-justify-content-end': !isMobileView,
                    }"
                  >
                    <p class="description">{{ $t("tour.digitalTicketText") }}</p>
                  </div>
                </div>

                <div class="separator mb-5"></div>

                <div
                  v-if="
                    tourData.isPass &&
                    tourData.highlights &&
                    tourData.highlights[0] &&
                    tourData.highlights[0].text
                  "
                >
                  <h2 class="mb-5 subtitle">{{ $t("tour.highlights") }}</h2>
                  <div class="description">
                    <div v-for="(item, i) in tourData.highlights" :key="i">
                      <div class="mb-1 highlights">
                        <img
                          src="../assets/check--green.svg"
                          class="mr-3 icon mt-1"
                        />
                        {{ item?.text ? item?.text : item }}
                      </div>
                    </div>
                  </div>
                  <div class="separator mb-5 mt-6"></div>
                </div>

                <div
                  v-if="tourData.whatsIncluded && tourData.whatsIncluded.length"
                  class="mb-5"
                >
                  <h2 class="mb-5 subtitle">{{ $t("tour.included") }}</h2>

                  <div
                    class="description"
                    v-if="Array.isArray(tourData.whatsIncluded)"
                  >
                    <div v-for="(item, i) in tourData.whatsIncluded" :key="i">
                      <div class="included-item">
                        <span class="check-badge" aria-hidden="true">
                          <img src="../assets/check--green.svg" />
                        </span>
                        <span>{{ item }}</span>
                      </div>
                    </div>
                  </div>

                  <span v-else class="description" v-html="tourData.whatsIncluded">
                    {{ tourData.whatsIncluded }}</span
                  >
                  <div class="separator mt-6 mb-5"></div>
                </div>

                <div
                  v-for="(collapse, index) of collapsableContent"
                  :key="index"
                  class="mb-5"
                >
                  <section v-if="collapse.text">
                    <p class="subtitle mb-5">
                      {{ collapse.title }}
                    </p>

                    <div class="description" v-if="Array.isArray(collapse.text)">
                      <div v-for="(item, i) in collapse.text" :key="i">
                        <div class="is-flex is-align-items-center">
                          <img src="../assets/check--green.svg" class="mr-3 icon" />
                          {{ item }}
                        </div>
                      </div>
                    </div>

                    <span v-else class="description" v-html="collapse.text">
                      {{ collapse.text }}</span
                    >
                    <div class="separator mb-5 mt-6"></div>
                  </section>
                </div>

                <div class="mb-6">
                  <div class="">
                    <h2 class="subtitle mt-3 mb-2">{{ $t("tour.meetingPoint") }}</h2>

                    <div
                      class="is-flex mb-4"
                      v-if="!tourData.isPass && tourData.meetingPoint"
                    >
                      <div class="is-flex is-align-items-center">
                        <a
                          class="link"
                          href="https://maps.google.com/?q=Pra%C3%A7a+da+Figueira,+1100-241+Lisboa,+Portugal&ftid=0xd1933877cc93d37:0x85fbf2cb1b9b558e"
                          target="_blank"
                          >{{ tourData.meetingPoint }}</a
                        >
                      </div>
                    </div>

                    <div
                      class="description mt-4"
                      v-else-if="tourData.stops && tourData.stops[0]"
                    >
                      {{ $t("tour.boardAnyStop") }}
                      <a
                        class="link"
                        target="_blank"
                        :href="tourData?.stops[0].direction"
                        >{{ tourData?.stops[0].title }}.</a
                      >
                    </div>
                  </div>

                  <GmapMap
                    v-if="!tourData.isPass"
                    style="width: auto; height: 350px"
                    :zoom="19"
                    :center="{
                      lat: 38.71364974975586,
                      lng: -9.138160705566406,
                    }"
                  >
                    <GmapMarker
                      :position="{
                        lat: 38.71364974975586,
                        lng: -9.138160705566406,
                      }"
                    />
                  </GmapMap>
                </div>

                <div v-if="similarActivities.length" class="mb-3 mt-6">
                  <div class="separator mt-6 mb-5"></div>
                  <p class="subtitle mt-4 mb-2">{{ $t("tour.youMightLike") }}</p>
                  <TicketsAndActivitiesContainer
                    :showMore="false"
                    class="mt-3"
                    :is-full-width="false"
                    :is-experiences-ids="false"
                    :Experiences="similarActivities"
                    :Tickets="[]"
                  />
                </div>
                <div
                  v-if="!tourData.isPass"
                  class="info--container mb-3 is-flex is-align-items-flex-start"
                >
                  <i18n path="tour.cantFind" tag="p" class="text">
                    <template #email>
                      <a class="link" href="mailto:support@guiders.pt">{{ $t("tour.emailUs") }}</a>
                    </template>
                    <template #whatsapp>
                      <a class="link" href="https://wa.me/351920043375" target="_blank">{{ $t("tour.whatsapp") }}</a>
                    </template>
                  </i18n>
                </div>

                <HpFAQ
                  v-if="!tourData.isPass"
                  class="faq mt-6"
                  :prop-questions="Questions"
                  :is-questions-opened="false"
                  is-full-width
                  :is-playfair="false"
                />
              </div>

              <div
                class="column sticky-column book-column is-hidden-touch mt-1"
                ref="stickyColumn"
              >
                <div class="booking-container">
                  <quote-request-modal
                    v-if="isUponRequest"
                    :tourData="rawTour"
                    :fullName="fullName"
                    :email="email"
                  />
                  <!-- Booking and quotes use the English tour: payments and team
                       emails stay in English. The visitor sees the title in
                       their language. -->
                  <booking-modal
                    v-else
                    :isBookingModalActive="isBookingModalActive"
                    :originalTourData="rawTour"
                    :displayTitle="tourData.title"
                    :fullName="fullName"
                    :email="email"
                    :phoneNumber="phoneNumber"
                  >
                  </booking-modal>
                  <div
                    v-if="!tourData.isPass && !isUponRequest"
                    class="is-flex is-items-align-center mt-5"
                  >
                    <img src="../assets/refund.svg" class="mr-2 icon--md" />{{ $t("tour.cancelPolicy") }}
                  </div>
                </div>
              </div>
            </div>

            <footer class="page-footer is-hidden-desktop">
              <div
                class="row footer-mobile"
                :class="{ 'pt-2 pb-2': !isMobileView, 'pt-1 pb-1': isMobileView }"
              >
                <div :class="{ 'mt-1 mb-1': !tourData.price }">
                  <div :class="{ 'is-5': !tourData.price, 'is-7': tourData.price }">
                    {{ $t("tour.from") }} <br v-if="tourData.isPass" />
                    <s
                      class="mr-2"
                      v-if="tourData.isPass && tourData.nonDiscountedPrice"
                      >{{
                        tourData?.nonDiscountedPrice
                          ? `${tourData?.nonDiscountedPrice}€`
                          : ""
                      }}</s
                    >
                    <span class="price">
                      {{
                        !tourData.isPrivateOnly || tourData.isPass
                          ? tourData.price
                          : tourData.privatePrice
                      }}€
                    </span>
                    <!-- <p
                      class="label mt-1 mr-2"
                      v-if="tourData.price && !tourData.isPass"
                    >
                      {{
                        isUponRequest
                          ? $t("tour.priceUponRequest") : $t("tour.smallDeposit")
                      }}
                    </p> -->
                  </div>
                </div>

                <b-button
                  class="is-large"
                  type="is-primary"
                  expanded
                  @click="isBookingModalActive = true"
                >
                  {{ isUponRequest ? $t("tour.requestQuote") : $t("tour.checkAvailability") }}
                </b-button>
              </div>
            </footer>
          </div>
        </div>

        <div v-else>
          <not-found />
        </div>

        <b-modal
          class="gh-modal"
          :class="{ 'is-modal-mobile': isMobileView }"
          v-model="isBookingModalActive"
          scroll="keep"
        >
          <quote-request-modal
            v-if="isUponRequest"
            :tourData="rawTour"
            :fullName="fullName"
            :email="email"
          />
          <booking-modal
            v-else
            :isBookingModalActive="isBookingModalActive"
            :originalTourData="rawTour"
            :displayTitle="tourData.title"
            :fullName="fullName"
            :email="email"
            :phoneNumber="phoneNumber"
          >
          </booking-modal>
        </b-modal>
      </div>
    </section>
  </GhPage>
</template>

<script>
import { db } from "../firebase";
import { mapMutations, mapState, mapActions } from "vuex";
import NotFound from "../components/PageNotFound.vue";
import BookingModal from "./elements/BookingModal.vue";
import QuoteRequestModal from "./elements/QuoteRequestModal.vue";
import { HelpersMixin } from "../mixins";
import GoBack from "./elements/GoBack.vue";
import {
  getHoursAndMinutes,
  convertMinutesToHoursAndMinutes,
  addDurationToTime,
  dateWithName,
} from "../helpers";
import { ActivityCategories as Categories } from "../enums/ActivityCategories";
import {
  ActivitiesMutations,
  ActivitiesActions,
  ActivitiesStates,
  UsersStates,
} from "../store/Storetypes";
import TicketsAndActivitiesContainer from "./TicketsAndActivitiesContainer.vue";
import HpFAQ from "./HpFAQ.vue";
import { Tours } from "@/components/Tours";
import { localizeTour, hasTranslation } from "@/i18n/tours";
import { DEFAULT_LOCALE, LOCALES, localePath } from "@/i18n";
import ImageGallery from "./elements/ImageGallery.vue";
import RouteMapView from "@/components/RouteMapView.vue";
import GhPage from "@/components/home/GhPage.vue";
//import StopAttractions from "./StopAttractions.vue";

export default {
  name: "TourDetails",
  components: {
    NotFound,
    BookingModal,
    QuoteRequestModal,
    GoBack,
    ImageGallery,
    TicketsAndActivitiesContainer,
    HpFAQ,
    //StopAttractions,
    RouteMapView,
    GhPage,
    VueLottie: () => import(/* webpackChunkName: "vue-lottie" */ "vue-lottie"),
  },
  metaInfo() {
    // Tours can carry their own SEO text in Firestore (`seo.metaTitle`,
    // `seo.metaDescription`). A metaTitle is written as the full title, so it
    // is used as-is; otherwise fall back to "<tour title> | Guiders.pt".
    // Missing or deleted tour: tell crawlers it's gone (real 404 via
    // Prerender.io) instead of indexing an empty page
    if (this.notFound) {
      return {
                title: this.$t("tour.notFound"),
        meta: [
          { vmid: "robots", name: "robots", content: "noindex, nofollow" },
          { vmid: "prerender-status-code", name: "prerender-status-code", content: "404" },
        ],
      };
    }

        // English pages use the tour's own SEO text; translated pages use the
    // translation's (or the translated title / description)
    const translation =
      (this.rawTour.translations && this.rawTour.translations[this.$i18n.locale]) || {};
    const seo =
      (this.$i18n.locale === DEFAULT_LOCALE ? this.rawTour.seo : translation.seo) || {};
    const fullTitle = seo.metaTitle || `${this.TourTitle} | Guiders.pt`;
    const description =
      seo.metaDescription || this.TourDescription.substring(0, 160);

    return {
      title: seo.metaTitle || this.TourTitle,
      titleTemplate: seo.metaTitle ? "%s" : "%s | Guiders.pt",
      meta: [
                // Not translated into this language yet: English text at a /pt...
        // address, so keep it out of search (canonical points to English)
        {
          vmid: "robots",
          name: "robots",
          content: this.isTranslated ? "index, follow, max-image-preview:large" : "noindex, follow",
        },
        { vmid: "description", name: "description", content: description },
        // OpenGraph (Facebook/AI Scrapers)
        { vmid: "og:type", property: "og:type", content: "website" },
        { vmid: "og:title", property: "og:title", content: fullTitle },
        {
          vmid: "og:description",
          property: "og:description",
          content: description,
        },
        { vmid: "og:image", property: "og:image", content: this.TourCover },
        { vmid: "og:url", property: "og:url", content: this.PageUrl },
        // Twitter
        { vmid: "twitter:card", name: "twitter:card", content: "summary_large_image" },
        { vmid: "twitter:title", name: "twitter:title", content: fullTitle },
        { vmid: "twitter:description", name: "twitter:description", content: description },
        { vmid: "twitter:image", name: "twitter:image", content: this.TourCover },
      ],
            link: [
        {
          vmid: "canonical",
          rel: "canonical",
          href: this.isTranslated ? this.PageUrl : this.tourUrl(DEFAULT_LOCALE),
        },
        ...this.languageLinks,
      ],
      script: [
        {
          // Product + TouristTrip: Google reads price and rating from a
          // Product (rich results), and TouristTrip tells search engines
          // and AI assistants this is a tour. Rating and offer must sit
          // directly on the item, not nested, or Google ignores them.
          vmid: "ld-tour",
          type: "application/ld+json",
          json: {
            "@context": "https://schema.org",
            "@type": ["Product", "TouristTrip"],
            "@id": `${this.PageUrl}#tour`,
            name: this.TourTitle,
            description: this.TourDescription || description,
            url: this.PageUrl,
            image: this.TourImages,
            brand: { "@type": "Brand", name: "Guiders.pt" },
            provider: { "@id": "https://guiders.pt/#organization" },
            touristType: this.tourData.isPrivateOnly ? "Private groups" : undefined,
            itinerary: this.tourData.stops?.length
              ? {
                  "@type": "ItemList",
                  itemListElement: this.tourData.stops.map((stop, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    name: stop.title || stop.name || `Stop ${i + 1}`,
                  })),
                }
              : undefined,
            aggregateRating: this.tourData.reviews?.length
              ? {
                  "@type": "AggregateRating",
                  ratingValue: this.overallRating,
                  reviewCount: this.tourData.reviews.length,
                  bestRating: "5",
                  worstRating: "1",
                }
              : undefined,
            offers: this.StructuredPrice
              ? {
                  "@type": "Offer",
                  price: this.StructuredPrice,
                  priceCurrency: "EUR",
                  availability: "https://schema.org/InStock",
                  url: this.PageUrl,
                }
              : undefined,
          },
        },
        {
          vmid: "ld-breadcrumbs",
          type: "application/ld+json",
          json: {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://guiders.pt/" },
              { "@type": "ListItem", position: 2, name: this.TourTitle, item: this.PageUrl },
            ],
          },
        },
        {
          vmid: "ld-faq",
          type: "application/ld+json",
          json: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: this.Questions.map((q) => ({
              "@type": "Question",
              name: q.title,
              acceptedAnswer: {
                "@type": "Answer",
                text: q.text.replace(/<[^>]*>?/gm, ""), // Strip HTML for AI reading
              },
            })),
          },
        },
      ],
    };
  },
  data() {
    return {
      lottieOptions: {
        animationData: require("@/assets/animations/loading-animation.json"),
        loop: true,
        autoplay: true,
      },
      showAttractionsForStop: null,
      stickyHeight: "auto",
      // The tour as stored in Firestore (English + `translations`).
      // `tourData` (computed) is the version in the current language.
      rawTour: {
        title: "",
        description: "",
        city: "",
        duration: 0,
        isPass: false,
        reviews: [],
        highlights: [],
        whatsIncluded: [],
        stops: [],
        galleryImages: [],
        videos: [],
        meetingPoint: "",
        price: 0,
        isPrivateOnly: false,
        privatePrice: 0,
        nonDiscountedPrice: 0,
        scheduleInfo: {
          firstDeparture: "",
          lastDeparture: "",
          frequency: "",
        },
        whatToExpect: [],
        additionalInfo: "",
        notSuitableFor: "",
        accessibility: "",
      },
      gallery: [],
      Tours,
      showMore: false,
      maxCharsToShow: 300,
      isGallery: false,
      firstPartDescription: "",
      secondPartDescription: "",
      isDescriptionSplit: false,
      isLoading: true,
      isGalleryLoaded: false,
      notFound: false,
      stars: null,
      comment: "",
      isSubmitButtonValid: false,
      loggedIn: false,
      profilePhoto: this.$cookies.get("smg_photoURL"),
      isBookingModalActive: false,
      fullName: "",
      email: "",
      phoneNumber: "",
      Categories,
      isOpen: -1,
      getHoursAndMinutes,
      convertMinutesToHoursAndMinutes,
      addDurationToTime,
      dateWithName,
      remainingTimesToRedeem: 0,
      couponName: undefined,
      discountPercentage: undefined,
      isMapView: false,
      hasError: false,
      errorMessage: "",
      loadTimeout: null,
    };
  },
  mixins: [HelpersMixin],
  computed: {
    ...mapState({
      user: (state) => state.users[UsersStates.USER],
      activities: (state) => state.activities[ActivitiesStates.ACTIVITIES],
    }),
    images() {
      let images = [];
      if (this.tourData.galleryImages) {
        this.tourData.galleryImages.forEach((img) =>
          images.push({
            href: img,
          }),
        );
      }
      return images;
    },
        // The tour in the current language (English where not translated)
    tourData() {
      return localizeTour(this.rawTour, this.$i18n.locale);
    },
    // Is there a translation for the current language?
    isTranslated() {
      return this.$i18n.locale === DEFAULT_LOCALE || hasTranslation(this.rawTour, this.$i18n.locale);
    },
    Questions() {
      return (this.$content("tour.faq") || []).map((item) => ({ title: item.q, text: item.a }));
    },
    ratings() {
      return this.tourData.reviews
        ? this.tourData.reviews.map((obj) => obj.stars)
        : [];
    },
    overallRating() {
      return this.getOverallRating(this.ratings);
    },
    TourTitle() {
            return this.tourData.title || this.$t("tour.loading");
    },
    TourDescription() {
      return this.tourData.description || "";
    },
    fullDescription() {
      return (this.tourData.description || "").trim();
    },
    // Short version for "Show more": the opening paragraph if it's not too
    // long, otherwise cut at the last whole word before the limit.
    descriptionPreview() {
      const text = this.fullDescription;
      if (text.length <= this.maxCharsToShow) return text;
      const firstParagraph = text.split(/\n\s*\n/)[0].trim();
      if (firstParagraph.length >= 120 && firstParagraph.length <= this.maxCharsToShow + 150) {
        return firstParagraph;
      }
      const cut = text.slice(0, this.maxCharsToShow);
      return cut.slice(0, Math.max(cut.lastIndexOf(" "), 0)).replace(/[\s,;:.\-•]+$/, "") + "…";
    },
    descriptionIsLong() {
      return this.descriptionPreview !== this.fullDescription;
    },
    // Tours arranged on request get a quote request form instead of the
    // booking/payment flow.
    isUponRequest() {
      return !!this.tourData.isUponRequest;
    },
    TourPrice() {
      return this.tourData.price || 0;
    },
    TourCover() {
      return this.tourData.coverImage || "";
    },
    // Cover first, then gallery photos: Google can pick the best fit
    TourImages() {
      const images = [this.tourData.coverImage, ...(this.tourData.galleryImages || [])];
      return [...new Set(images.filter(Boolean))].slice(0, 6);
    },
    // Price shown in search results: the price the page shows ("from X€").
    // None for tours sold on request, since there's no fixed price.
    StructuredPrice() {
      if (this.tourData.isUponRequest) return null;
      const price =
        !this.tourData.isPrivateOnly || this.tourData.isPass
          ? this.tourData.price
          : this.tourData.privatePrice;
      return price || null;
    },
    // One URL per tour, built from its id and current title (the same slug
    // the sitemap and tour cards use). Old or mistyped slugs in the address
    // bar all point search engines back to this one.
        PageUrl() {
      return this.tourUrl(this.$i18n.locale);
    },
        // hreflang: English plus every language this tour is translated into
    languageLinks() {
      const available = LOCALES.filter(
        (l) => l.code === DEFAULT_LOCALE || hasTranslation(this.rawTour, l.code),
      );
      if (available.length < 2) return [];
      return [
        ...available.map((l) => ({
          vmid: `alternate-${l.code}`,
          rel: "alternate",
          hreflang: l.code,
          href: this.tourUrl(l.code),
        })),
        {
          vmid: "alternate-x-default",
          rel: "alternate",
          hreflang: "x-default",
          href: this.tourUrl(DEFAULT_LOCALE),
        },
      ];
    },
    TourKeywords() {
      return this.tourData?.keywords?.length ? this.tourData?.keywords : "";
    },
    similarActivities() {
      let similarActivities = [];
      if (this.tourData.isPass) {
        similarActivities = this.activities.filter(
          (activity) =>
            activity.isPass && activity.id !== this.$route.params.id,
        );
        return similarActivities;
      }
      const otherActivities = this.activities.filter(
        (activity) => activity.id !== this.tourData.id,
      );
      otherActivities.map((activity) => {
        if (
          activity.categories &&
          activity.categories.some((category) =>
            this.tourData.categories.includes(category),
          )
        ) {
          similarActivities.push(activity);
        }
      });
      return similarActivities.slice(0, 5);
    },
    reviewData() {
      const { stars, comment } = this;
      return {
        stars,
        comment,
      };
    },
    collapsableContent() {
      if (!this.tourData) return [];
      const content = [];
      if (this.tourData.whatToExpect && this.tourData.whatToExpect.length) {
        content.push({
                    title: this.$t("tour.whatToExpect"),
          text: this.tourData.whatToExpect,
        });
      }
      if (this.tourData.additionalInfo) {
        content.push({
                    title: this.$t("tour.additionalInfo"),
          text: this.tourData.additionalInfo,
        });
      }
      if (this.tourData.notSuitableFor) {
        content.push({
                    title: this.$t("tour.notSuitableFor"),
          text: this.tourData.notSuitableFor,
        });
      }
      if (this.tourData.accessibility) {
        content.push({
                    title: this.$t("tour.accessibility"),
          text: this.tourData.accessibility,
        });
      }

      return content;
    },
  },
  mounted() {
    this.updateStickyHeight();
    window.addEventListener("resize", this.updateStickyHeight);
  },
  created() {
    console.log("🚀 TourDetails component created");

    // Set a timeout to force loading to stop after 8 seconds
    this.loadTimeout = setTimeout(() => {
      if (this.isLoading) {
        console.log("⏰ Load timeout reached - forcing stop");
        this.isLoading = false;
        this.hasError = true;
        this.errorMessage =
          "Loading took too long. The tour might not exist or there's a connection issue.";

        // Try to use fallback data if available
        this.useFallbackData();
      }
    }, 8000);
    this.getActivities();
    window.scrollTo(0, 0);

    // Tour ID from the route (/details/<id>/... or /es/details/<id>/...).
    // Falls back to the segment after "details" in the address.
    const pathParts = this.$route.path.split("/");
    const idFromPath =
      this.$route.params.id || pathParts[pathParts.indexOf("details") + 1];

    if (idFromPath && idFromPath !== "undefined") {
      console.log("📝 Activity ID from path:", idFromPath);
      this.prepareActivityData(idFromPath);
    } else {
      console.error("❌ No valid activity ID found in route");
      this.isLoading = false;
      this.notFound = true;
    }

    this.fullName = this.user?.displayName || "";
    this.email = this.user?.email || "";
  },
  beforeDestroy() {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
    }
    window.removeEventListener("resize", this.updateStickyHeight);
  },
  watch: {
    reviewData: {
      handler: function () {
        if (this.stars !== null && this.comment.length > 0) {
          this.isSubmitButtonValid = true;
        } else {
          this.isSubmitButtonValid = false;
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      getActivities: ActivitiesActions.GET_ACTIVITIES,
    }),
    ...mapMutations({
      getActivityData: ActivitiesMutations.GET_ACTIVITY_DATA,
    }),

    retryLoading() {
      this.hasError = false;
      this.isLoading = true;
      const activityId = this.$route.params.id;
      if (activityId) {
        this.prepareActivityData(activityId);
      }
    },

    useFallbackData() {
      console.log("🔄 Using fallback data");
      this.rawTour = {
        isFeatured: false,
        sharedTimeSlots: ["10:00", "14:00"],
        reports: [],
        categories: [0],
        duration: "1440",
        priceLink: "price_1T6uS7Jou6FLnGKIszpxaaDS",
        privatePrice: 0,
        nonDiscountedPrice: null,
        coverImage:
          "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/gallery%2Fpeople%2FFrame%2027.svg?alt=media&token=3abb6673-b445-4d6b-9596-f72b3c1fb03e",
        guiderPayment: 0,
        meetingPoint:
          "Hard Rock Cafe, Lisboa Avenida da Liberdade 2, 1250-144 Lisboa",
        languages: ["English"],
        stars: null,
        description:
          "With the LISBON VISITOR PASS®, you’re free to ride around the city in a tuk tuk as much as you want. Take your time, explore at your own pace, and let our friendly local guides show you the coolest spots, stories, and legends of Lisbon.",
        id: "Wtc6b2YdHZo9lvHllBuZ",
        evaluations: [],
        minNumOfParticipants: 2,
        isPass: true,
        stops: [
          {
            imageUrl:
              "https://external-content.duckduckgo.com/iu/?u=https%3A%2F%2Fstorage.alboom.ninja%2Fsites%2F17270%2Falbuns%2F641155%2F002_cr7_00059_20200212.jpg%3Ft%3D1588114674&f=1&nofb=1&ipt=86639b7481146fd6886f0ac52814047c26c8a39dd6fcf91b44710af3f8cce17a",
            canCall: true,
            title: "Pestana CR7 Lisboa",
            direction:
              "https://www.google.com/maps/place/Pestana+CR7+Lisboa/@38.7091765,-9.1385316,17z/data=!3m1!4b1!4m9!3m8!1s0xd1934798516a6bf:0xca2744d48a69d3e7!5m2!4m1!1i2!8m2!3d38.7091723!4d-9.1359567!16s%2Fg%2F11bz_qzqzl?entry=ttu&g_ep=EgoyMDI1MDUyOC4wIKXMDSoASAFQAw%3D%3D",
            description: "",
            id: 1748905267296,
            attractions: [
              {
                name: "Lisbon: Chiado & Principe Real Audio Tour",
                href: "https://wegotrip.com/lisbon-d17/uncover-lisbons-charms-a-cultural-in-app-audio-tour-p7478/?coupon=5554",
                price: 10,
                image: "",
              },
            ],
          },
          {
            description: "",
            title: "Praça do Comércio",
            direction:
              "https://www.google.com/maps/place/Pra%C3%A7a+do+Com%C3%A9rcio/@38.7072869,-9.1412322,17z/data=!3m1!4b1!4m6!3m5!1s0xd19347a30a54b63:0x30532f5f6b28e044!8m2!3d38.7072828!4d-9.1363613!16zL20vMGczbDNz?entry=ttu&g_ep=EgoyMDI1MDUyOC4wIKXMDSoASAFQAw%3D%3D",
            imageUrl:
              "https://cdn.pixabay.com/photo/2020/03/19/22/39/place-du-commerce-4948991_1280.jpg",
            id: 1748900724373,
          },
          {
            imageUrl:
              "https://external-content.duckduckgo.com/iu/?u=https%3A%2F%2Foffloadmedia.feverup.com%2Flisboasecreta.co%2Fwp-content%2Fuploads%2F2022%2F11%2F08084558%2FArco-da-Rua-Augusta-%2540joao-reguengos-scaled.jpg&f=1&nofb=1&ipt=3b6825dd0e22f12696046ddec38b844f9e2c3066801cf7c290d87268e167302f",
            title: "Arco da Augusta",
            direction:
              "https://www.google.com/maps/place/Arco+da+Rua+Augusta/@38.7084489,-9.1393992,17z/data=!3m1!4b1!4m6!3m5!1s0xd193479870cf7e5:0xc4e62457fc6249b0!8m2!3d38.7084447!4d-9.1368243!16s%2Fm%2F0fqm77x?entry=ttu&g_ep=EgoyMDI1MDUyOC4wIKXMDSoASAFQAw%3D%3D",
            description: "",
            id: 1748900746432,
            attractions: [
              {
                href: "https://www.tiqets.com/en/lisbon-attractions-c76528/tickets-for-rua-augusta-arch-viewpoint-p974825/?partner=guiders.pt",
                price: 4.5,
                name: "Rua Augusta Arch Viewpoint",
              },
            ],
          },
          {
            imageUrl:
              "https://external-content.duckduckgo.com/iu/?u=https%3A%2F%2Fstoantoniolisboa.com%2Fwp-content%2Fuploads%2F2022%2F02%2FSanto-Antonio-igreja_lv-2048x1462.jpg&f=1&nofb=1&ipt=37790dce2d0ca1b14e2549a61dc4c5d2a5c03f3a09c885932a79c36f2ddcdcdf",
            description: "",
            title: "Igreja Santo António",
            direction:
              "https://www.google.com/maps/place/Igreja+de+Santo+Ant%C3%B3nio+de+Lisboa/@38.7099764,-9.1364038,17z/data=!3m1!4b1!4m6!3m5!1s0xd1ed9c110ae95f7:0x914d1a47298855d7!8m2!3d38.7099722!4d-9.1338289!16s%2Fm%2F02w0q6n?entry=ttu&g_ep=EgoyMDI1MDUyOC4wIKXMDSoASAFQAw%3D%3D",
            id: 1748900827657,
          },
          {
            attractions: [
              {
                href: "https://www.tiqets.com/en/lisbon-attractions-c76528/tickets-for-lisbon-cathedral-p1028502/?partner=guiders.pt",
                price: 7,
                name: "Lisbon Cathedral: Entry Ticket",
              },
            ],
            id: "2",
            title: "Sé de Lisboa",
            direction:
              "https://www.google.com/maps/place/S%C3%A9+de+Lisboa/@38.7098339,-9.1355284,17z/data=!3m1!4b1!4m6!3m5!1s0xd1934773e51dc9b:0x926e1d1ce1b76219!8m2!3d38.7098339!4d-9.1329535!16zL20vMDFmMnp6?entry=ttu&g_ep=EgoyMDI1MDMxOC4wIKXMDSoASAFQAw%3D%3D",
            canCall: true,
            description: "",
            imageUrl:
              "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/locations%2Fse.png?alt=media&token=d02a8b2b-e03a-4143-a0ea-23c199262ec2",
          },
          {
            direction:
              "https://www.google.com/maps/place/Castelo+de+S.+Jorge/@38.7139134,-9.1360511,17z/data=!3m1!4b1!4m6!3m5!1s0xd193477b40ec39b:0xb4c0704199e433d7!8m2!3d38.7139092!4d-9.1334762!16zL20vMDgybDJw?entry=ttu&g_ep=EgoyMDI1MDUyOC4wIKXMDSoASAFQAw%3D%3D",
            title: "Castelo São Jorge",
            description: "",
            imageUrl:
              "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/locations%2Fsjorge.png?alt=media&token=ea8763a8-77fc-4439-aba5-4a682a69ec92",
            attractions: [
              {
                price: 6,
                href: "https://www.tiqets.com/en/lisbon-attractions-c76528/tickets-for-tower-of-the-church-of-st-george-s-castle-p1028501/?partner=guiders.pt",
                name: "Tower of Saint George’s Castle Church + Drink",
              },
              {
                name: "São Jorge Castle: Entry + Audio Guide",
                href: "https://www.tiqets.com/en/lisbon-attractions-c76528/tickets-for-castelo-de-sao-jorge-entry-audio-guide-p1106063/?partner=guiders.pt",
                price: 26,
              },
            ],
            id: 1748901263884,
          },
          {
            imageUrl:
              "https://external-content.duckduckgo.com/iu/?u=https%3A%2F%2Ftse1.mm.bing.net%2Fth%3Fid%3DOIP.dD1a8I1OCOBp2Shm2y5iYgHaE8%26r%3D0%26pid%3DApi&f=1&ipt=d52f75632f2bd4ec26f914e37f4abde4fb1ffd1bdd0e1444353e863707001a7d",
            title: "Miradouro Santa Luzia",
            direction:
              "https://www.google.com/maps/place/Miradouro+de+Santa+Luzia/@38.7116998,-9.1327721,17z/data=!3m1!4b1!4m6!3m5!1s0xd1933849770e4c7:0x41203b21edcd2769!8m2!3d38.7116956!4d-9.1301972!16s%2Fg%2F1216ftv9?entry=ttu&g_ep=EgoyMDI1MDUyOC4wIKXMDSoASAFQAw%3D%3D",
            description: "",
            id: 1748901308587,
          },
          {
            description: "",
            title: "Miradouro das Portas do Sol",
            canCall: true,
            direction:
              "https://www.google.com/maps/place/Miradouro+das+Portas+do+Sol/@38.7119253,-9.1325317,17z/data=!3m1!4b1!4m6!3m5!1s0xd193476669b0c97:0xbb86a342b289f3c2!8m2!3d38.7119253!4d-9.1299568!16s%2Fg%2F11bccjdmgz?entry=ttu&g_ep=EgoyMDI1MDMxOC4wIKXMDSoASAFQAw%3D%3D",
            imageUrl:
              "https://lh3.googleusercontent.com/gps-cs-s/AHRPTWnFY4xKF3aLNaJRp7b0RaHNqzt8Ovm1l1QLY3nfMBNada-6mCQohKUl1cYQdoa_af20iaLnU5GJYpK3n0tsSqRhiIeJ5BKf0D0wCWzY3xNes1T5Ux5n64a-ZSswP63tzd--Q3ja=s1360-w1360-h1020-rw",
          },
          {
            description: "",
            canCall: true,
            direction:
              "https://www.google.com/maps/place/Miradouro+da+Gra%C3%A7a/@38.716559,-9.1342239,17z/data=!3m1!4b1!4m6!3m5!1s0xd193389ab8d3963:0x46c80fef319cf9f0!8m2!3d38.716559!4d-9.131649!16s%2Fg%2F11_p4_gll?entry=ttu&g_ep=EgoyMDI1MDMxOC4wIKXMDSoASAFQAw%3D%3D",
            title: "Miradouro da Graça",
            imageUrl: "https://www.lisbonportugaltourism.com/images/graca.jpg",
          },
          {
            attractions: [
              {
                href: "https://www.tiqets.com/en/lisbon-attractions-c76528/tickets-for-monastery-of-saint-vincent-de-fora-entry-p1113405/?partner=guiders.pt",
                price: 8,
                name: "Monastery of Saint Vincent de Fora: Entry Ticket",
              },
            ],
            imageUrl:
              "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/locations%2Fsvfora.png?alt=media&token=33453ae6-1330-435b-8d04-b3bd4a25532a",
            title: "Mosteiro de São Vicente de Fora",
            canCall: true,
            direction:
              "https://www.google.com/maps/place/Mosteiro+de+S%C3%A3o+Vicente+de+Fora/@38.7144957,-9.1306614,17z/data=!3m1!4b1!4m6!3m5!1s0xd1933c5dc7a6049:0x18b50cd3120e1d4a!8m2!3d38.7144957!4d-9.1280865!16s%2Fg%2F11fwblh970?entry=ttu&g_ep=EgoyMDI1MDMxOC4wIKXMDSoASAFQAw%3D%3D",
            description: "",
          },
          {
            imageUrl:
              "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/locations%2Fpantheon.png?alt=media&token=ca72e374-23db-45e6-82e0-71456be21128",
            canCall: true,
            direction:
              "https://www.google.com/maps/place/Pante%C3%A3o+Nacional/@38.7149944,-9.1272584,17z/data=!3m1!4b1!4m6!3m5!1s0xd19338aa3ea9f7d:0xbedb8a4f3ced4e37!8m2!3d38.7149944!4d-9.1246835!16zL20vMGZ4anRf?entry=ttu&g_ep=EgoyMDI1MDMxOC4wIKXMDSoASAFQAw%3D%3D",
            title: "Panteão Nacional",
            description:
              "Along the way, we’ll pass through:\n\n- Santa Clara\n- Afama\n- Graça\n\nFeel free to hop off and explore any of these charming neighborhoods - just let your guide know, and they’ll be happy to stop for you!",
          },
          {
            imageUrl:
              "https://informacoeseservicos.lisboa.pt/fileadmin/espacos/user_upload/senhora_do_monte_vista_3.jpg",
            title: "Miradouro da Senhora do Monte",
            direction:
              "https://www.google.com/maps/place/Miradouro+da+Senhora+do+Monte/@38.7192133,-9.1354047,17z/data=!3m1!4b1!4m6!3m5!1s0xd19338f34837d57:0x611f8deb59a26af4!8m2!3d38.7192133!4d-9.1328298!16s%2Fg%2F120zpds0?entry=ttu&g_ep=EgoyMDI1MDMxOC4wIKXMDSoASAFQAw%3D%3D",
            canCall: true,
            description:
              "Along the way, we’ll pass through Mouraria\n\nFeel free to hop off and explore - just let your guide know, and they’ll be happy to stop for you!",
          },
          {
            imageUrl:
              "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/locations%2Ffigueira.png?alt=media&token=c408d0f9-e7ab-4d1b-995c-40decf2487d1",
            description: "",
            direction:
              "https://www.google.com/maps/place/Pra%C3%A7a+da+Figueira,+1100-241+Lisboa/@38.7134814,-9.1381395,17z/data=!3m1!4b1!4m6!3m5!1s0xd1933877cc93d37:0x85fbf2cb1b9b558e!8m2!3d38.7134814!4d-9.1381395!16s%2Fm%2F02v_1wf?entry=ttu&g_ep=EgoyMDI1MDMxOC4wIKXMDSoASAFQAw%3D%3D",
            title: "Praça da Figueira",
            canCall: true,
            id: "1",
          },
          {
            id: 1748905242923,
            attractions: "",
            imageUrl:
              "https://external-content.duckduckgo.com/iu/?u=https%3A%2F%2Fstorage.alboom.ninja%2Fsites%2F17270%2Falbuns%2F641155%2F002_cr7_00059_20200212.jpg%3Ft%3D1588114674&f=1&nofb=1&ipt=86639b7481146fd6886f0ac52814047c26c8a39dd6fcf91b44710af3f8cce17a",
            title: "Pestana CR7 Lisboa",
            direction:
              "https://www.google.com/maps/place/Pestana+CR7+Lisboa/@38.7091765,-9.1385316,17z/data=!3m1!4b1!4m9!3m8!1s0xd1934798516a6bf:0xca2744d48a69d3e7!5m2!4m1!1i2!8m2!3d38.7091723!4d-9.1359567!16s%2Fg%2F11bz_qzqzl?entry=ttu&g_ep=EgoyMDI1MDUyOC4wIKXMDSoASAFQAw%3D%3D",
            canCall: true,
            description: "",
          },
        ],
        sharedPriceLink: null,
        isPrivateOnly: true,
        city: "Lisbon, Portugal",
        highlights: [
          {
            text: "Hop on and off as many times as you like, all day.",
          },
          {
            text: "Explore at your own pace without fixed schedules.",
          },
          {
            text: "Stop anywhere for amazing pictures and to explore, no strict route!",
          },
          {
            text: "Friendly local guide",
          },
        ],
        scheduleInfo: {
          lastDeparture: "17:30",
          frequency: "Every 30 minutes",
          firstDeparture: "10:00",
        },
        cancellationPolicy:
          "Cancellations are non-refundable but eligible for an e-voucher of the same value. The voucher can be used toward any of our services within 12 months.",
        maxNumOfParticipants: 4,
        whatsIncluded: "",
        marker: {
          lng: -9.138160705566406,
          lat: 38.71364974975586,
        },
        title: "LISBON VISITOR PASS ®: Tuk Tuk",
        price: 30,
        whatToExpect: "",
        galleryImages: [
          "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/gallery%2Fpeople%2FFrame%2027.svg?alt=media&token=3abb6673-b445-4d6b-9596-f72b3c1fb03e",
          "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/guiders%2F6 (1).webp?alt=media&token=8dc4e851-3865-4698-b08c-b1fc039fcdce",
          "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/guiders%2F5 (1).webp?alt=media&token=2806d95d-6683-414f-bd8b-43b6972aefc4",
          "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/guiders%2F4 (1).webp?alt=media&token=6a247f61-71a1-4eea-983e-c0c51baf2b78",
          "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/guiders%2F3 (1).webp?alt=media&token=87895f85-9729-4c45-a5ef-5b010bfb6517",
          "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/guiders%2F2 (1).webp?alt=media&token=454cb0ff-e65c-4e19-8fc3-e6722219a093",
          "https://firebasestorage.googleapis.com/v0/b/tours-26f02.appspot.com/o/guiders%2F1 (1).webp?alt=media&token=91c992df-52b2-4c72-a1e1-649049d046fd",
        ],
        additionalInfo:
          "Once you’ve made your purchase, we’ll email you an e-ticket — just have it handy (on your phone or printed) when you hop on. To avoid waiting and guarantee a seat, request a driver from any pickup point directly on your e-ticket page before you’re ready to ride again.",
        accessibility: "",
        privateTimeSlots: [
          "10:00",
          "11:00",
          "12:00",
          "13:00",
          "14:00",
          "15:00",
          "16:00",
          "17:00",
          "18:00",
        ],
        notSuitableFor:
          "⛔ People with severe mobility impairments   <br/> ⛔ Infants under 3 years old (for safety reasons)",
        marketPrice: 0,
        isVisible: true,
      };
      this.notFound = false;
      this.hasError = false;
      this.isLoading = false;
      this.prepareGallery();
    },

    toggleAttractions(stop) {
      if (this.showAttractionsForStop === stop.title) {
        this.showAttractionsForStop = null;
      } else {
        this.showAttractionsForStop = stop.title;
      }
    },

    updateStickyHeight() {
      if (this.$refs.leftColumn) {
        this.stickyHeight = `${this.$refs.leftColumn.offsetHeight}px`;
      }
    },

    async prepareActivityData(activityId) {
      console.log("🔍 Starting prepareActivityData with ID:", activityId);

      try {
        this.hasError = false;
        this.isLoading = true;
        this.notFound = false;

        if (!activityId || activityId === "undefined") {
          console.error("❌ No valid activity ID provided");
          this.notFound = true;
          this.isLoading = false;
          return;
        }

        // Clean the ID
        const cleanId = activityId.trim();
        console.log("📡 Fetching from Firebase with clean ID:", cleanId);

        // Check if Firebase is initialized
        if (!db) {
          throw new Error("Firebase not initialized");
        }

        // Add a timeout for the Firebase request. The build pre-renders ~100
        // pages in a row, so it waits longer and tries twice: the page it
        // saves must have the real tour (title = canonical, translations).
        const prerendering = !!window.__PRERENDER_INJECTED;
        const fetchTour = () =>
          Promise.race([
            db.firestore().collection("tours").doc(cleanId).get(),
            new Promise((_, reject) => {
              setTimeout(() => reject(new Error("Firebase request timeout")), prerendering ? 20000 : 5000);
            }),
          ]);

        const docRef = await fetchTour().catch((error) => {
          if (!prerendering) throw error;
          console.warn("Retrying the tour request", error.message);
          return fetchTour();
        });
        console.log("📄 Firebase response - exists:", docRef.exists);

        if (docRef.exists) {
          const tourData = docRef.data();
          console.log({ tourData });

          console.log(
            "🎯 Document data received, isVisible:",
            tourData.isVisible,
          );

          if (tourData.isVisible !== false) {
            this.rawTour = { ...this.rawTour, ...tourData };
            this.notFound = false;
            console.log("✅ Tour data set successfully");

            // Prepare gallery without waiting
            this.prepareGallery().catch((error) => {
              console.error("Gallery preparation failed:", error);
              this.isGalleryLoaded = true;
            });

            this.formatTourDetails();
          } else {
            console.log("🚫 Tour is not visible");
            this.notFound = true;
          }
        } else {
          console.log("❌ Document does not exist in Firebase");
          this.notFound = true;
        }
      } catch (error) {
        console.error("💥 Error in prepareActivityData:", error);
        this.hasError = true;
        this.errorMessage = "Failed to load activity details: " + error.message;
        this.notFound = true;

        // If Firebase fails, try to use local data. Never while pre-rendering:
        // that copy has old titles and no translations, so the saved page
        // would point its canonical to an old address (the SEO check fails
        // the build instead, so it can be run again).
        const localTour = window.__PRERENDER_INJECTED ? null : Tours.find((tour) => tour.id === activityId);
        if (localTour) {
          console.log("🔄 Using local tour data as fallback");
          this.rawTour = { ...this.rawTour, ...localTour };
          this.notFound = false;
          this.hasError = false;
          this.prepareGallery();
        }
      } finally {
        console.log("🏁 Setting isLoading to false");
        this.isLoading = false;

        // Clear the timeout
        if (this.loadTimeout) {
          clearTimeout(this.loadTimeout);
        }
      }
    },

    async prepareGallery() {
      try {
        console.log("🖼️ Preparing gallery...");
        this.isGalleryLoaded = false;

        const images = this.tourData?.galleryImages || [];
        const videos = this.tourData?.videos || [];

        console.log("Found images:", images.length, "videos:", videos.length);

        // Simple approach - don't wait for images to load
        this.gallery = [
          ...videos.map((url) => ({ url, type: "video" })),
          ...images.map((url) => ({ url, type: "image" })),
        ];

        console.log("✅ Gallery prepared with", this.gallery.length, "items");
        this.isGalleryLoaded = true;
      } catch (error) {
        console.error("❌ Error in prepareGallery:", error);
        this.gallery = [];
        this.isGalleryLoaded = true;
      }
    },

        // "3 hrs" in English; "3 h" / "1 h 30 min" elsewhere
    durationText(minutes) {
      if (this.$i18n.locale === DEFAULT_LOCALE) return this.getHoursAndMinutes(minutes);
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours && mins) return `${hours} h ${mins} min`;
      return hours ? `${hours} h` : `${mins} min`;
    },
    // The tour's address in a language. The slug always comes from the
    // English title, so every language shares one URL pattern.
    tourUrl(locale) {
      const { id } = this.$route.params;
      const isTourRoute = ["details", "details-lang"].includes(this.$route.name);
      if (!this.rawTour.title || !id || !isTourRoute) {
        return `https://guiders.pt${this.$route.path}`;
      }
      const path = `/details/${id}/${encodeURI(this.slugify(this.rawTour.title))}`;
      return `https://guiders.pt${localePath(path, locale)}`;
    },
    formatTourDetails() {
      if (this.tourData.whatsIncluded) {
        if (this.tourData.whatsIncluded.length >= 450) {
          this.isDescriptionSplit = true;

          let firstPeriod = this.tourData.whatsIncluded
            .substring(0, 425)
            .lastIndexOf(".");
          if (firstPeriod !== -1) {
            this.firstPartDescription = this.tourData.whatsIncluded.substring(
              0,
              firstPeriod + 1,
            );
            this.secondPartDescription = this.tourData.whatsIncluded.substring(
              firstPeriod + 1,
            );
          } else {
            this.firstPartDescription = this.tourData.whatsIncluded.substring(
              0,
              400,
            );
            this.secondPartDescription =
              this.tourData.whatsIncluded.substring(400);
          }
        }
      }
    },

    toggleShowMore() {
      this.showMore = !this.showMore;
    },

    switchGallery(value) {
      this.isGallery = value;
      if (value) {
        document.documentElement.classList.add("is-clipped");
      } else {
        document.documentElement.classList.remove("is-clipped");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

// Page layout inside GhPage (header + footer come from GhPage)
.tour-page {
  padding: 2rem 1.5rem 4rem;
}

@include touch {
  .tour-page { padding: 0 1.25rem 2rem; } // 20px: matches the -20px bleed of the card rows
  // Room for the fixed "Check availability" bar
  .tour-page-shell { padding-bottom: 5.5rem; }
  .carousel-skelton-mobile { margin: 0 -1.25rem; }
}

.loading-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.error-state {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

#content {
  opacity: 0;
  animation: fadeIn 0.3s ease-in forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

.attractions-container {
  border-radius: 12px;
  padding: 1.5rem 0;
  margin: 1rem 0;
}

.attractions-container::before {
  content: "";
  display: block;
  height: 3px;
  background: linear-gradient(90deg, $forest-2, $lime);
  border-radius: 3px 3px 0 0;
  margin: -1.5rem -1.5rem 1.5rem -1.5rem;
}

.loading-placeholder {
  min-height: 100vh;
  display: flex;
}

.sticky-column {
  position: sticky;
  top: 96px;
  width: 300px;
  align-self: flex-start;
  overflow-y: auto;
}

.highlights {
  display: flex;
  align-items: center;
  .icon {
    place-self: start;
  }
}

.gray-container {
  border-radius: $radius-large;
  padding: 1.25rem 1rem;
  background-color: $soft;
  .icon-md {
    border-radius: $radius-rounded;
    padding: 0.5rem;
    height: 2.4rem;
    background-color: $lime;
    &.green,
    &.purple,
    &.blue {
      background-color: $white;
    }
  }
}

.route-info-container {
  border-radius: $radius;
  padding: 1rem;
  min-width: 9rem;
  margin: 0 0.5rem;
  margin-bottom: 2rem;
  font-size: 0.8rem;
  background-color: $soft;
  .info-title {
    font-size: 0.9rem;
    color: $ink-sub;
    padding-bottom: 0.5rem;
  }
  .info-subtitle {
    font-size: 0.95rem;
    font-weight: 600;
  }
  &:first-of-type {
    margin-left: 0 !important;
  }
}

.activity--duration {
  color: $forest;
  border-radius: $radius-rounded;
  background-color: $soft;
  padding: 6px 15px;
  font-weight: 600;
  width: -moz-max-content;
  width: max-content;
  .duration {
    font-size: 0.8rem;
    color: $forest;
  }
  .icon {
    height: 0.8rem;
    width: 0.8rem;
    object-fit: fill;
  }
}

.tour-description {
  margin: 1.5rem 0 2.5rem;

  .description { margin-bottom: 0.5rem; }
}

// What's included: each tick sits in a light green circle
.included-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.625rem;
  color: $ink-sub;
  white-space: normal;
}

.check-badge {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  margin-top: 0.05rem;
  border-radius: $radius-rounded;
  background: mix($lime, $white, 35%);
  display: flex;
  align-items: center;
  justify-content: center;

  img { width: 0.8rem; height: 0.8rem; }
}
.lottie-an {
  margin: 0;
}

.fixed {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
}

.more {
  cursor: pointer;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 700;
  color: $forest;
  text-decoration: underline;

  &:hover { color: $forest-2; }
}

.container--outlined {
  box-shadow: none;
  border-radius: 10px;
  font-weight: 600;
  padding: 15px;
  border: none;
}

.back--btn {
  z-index: 2;
  position: relative;
  top: 12px;
  display: block;
}

// Phones: photos start right under the header, with the back button
// floating over the top-left corner of the carousel.
.has-overlay-back {
  position: relative;

  > .columns {
    margin-top: 0 !important;

    > .column.is-8 {
      padding-top: 0;
    }
  }

  .back--btn {
    position: absolute;
    top: 0.75rem;
    left: -0.5rem; // 12px from the screen edge (page padding is 20px)
    width: auto;
    height: auto;
    z-index: 5;
  }

  ::v-deep .back--btn .clickable-icon {
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    background-color: rgba($white, 0.94);
    color: $forest;
    box-shadow: 0 2px 8px rgba($forest, 0.18);

    .icon {
      width: 1rem;
      height: 1rem;
    }

    i {
      font-size: 1rem;
    }
  }
}

.link {
  color: $forest-2;
}

.map-container {
  padding: 15px 20px;
  border-radius: 0 0 20px 20px;
  margin-bottom: 1.5rem;
}

.danger--container {
  background-color: #ff7272;
  color: #fff;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-weight: 400;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  .sale--icon {
    margin-top: 3px;
    place-self: flex-start;
    height: 1rem;
    width: 1rem;
  }
}

.icon {
  height: 1.15rem;
  width: 1.15rem;
}

.icon--md {
  height: 1.75rem;
  width: 1.75rem;
}

.booking-container {
  border-radius: $radius-large;
  position: sticky;
  top: 96px;
  width: -webkit-fill-available;
}

.button.is-large {
  height: 2.8rem;
  max-width: 11rem;
  font-size: 1rem;
}

.rounded-image {
  height: 282px;
  object-fit: cover;
}

.title-with-icon {
  .description {
    margin-left: 2.6rem;
  }
}

// Tour name: same heading style as the homepage section titles
.title {
  font-weight: 800 !important;
  font-size: 2.25rem !important;
  line-height: 1.1;
  letter-spacing: -0.02em;

  @include mobile { font-size: 1.75rem !important; }
}

.stop--image {
  height: 16rem;
  width: -webkit-fill-available;
  object-fit: cover;
  border-radius: $radius-large;
}

@media (min-width: 764px) {
  .stop--image {
    height: 20rem;
  }
}

@media (min-width: 1024px) {
  .stop--image {
    width: 28rem;
    -o-object-fit: cover;
    object-fit: cover;
  }
}

// Block headings (Itinerary, Description, What is included...)
.subtitle,
.card-header-title {
  font-size: 1.375rem;
  margin-bottom: 0.5rem;
  letter-spacing: -0.01em;
}

.description {
  font-weight: 400;
  white-space: pre-line;
  color: $ink-sub;
  line-height: 1.6;
}

.tour-details {
  position: absolute;
  top: 24px;
  right: 18px;
  z-index: 2;
  .tag {
    color: $forest;
    background-color: $white;
    padding: 4px 14px;
    border-radius: $radius-rounded;
    font-size: 15px;
  }
  .icon {
    margin-right: -6px;
    margin-left: -6px;
  }
}

.card-header {
  box-shadow: none;
}

.card-header-title,
.subtitle,
.title {
  color: $forest;
  font-weight: 800;
}

.card-header-title,
.card-header-icon,
.card-content {
  padding: 0;
}

.card-header-title {
  font-size: 1.05rem;
}

.languages-container {
  background-color: $soft;
  padding: 12px;
  border-radius: $radius-large;
}

::v-deep {
  .faq {
    .title {
      font-size: 1.375rem;
    }
    .card-header-title {
      font-size: 0.95rem;
    }
  }
  .about-section {
    .b-tabs .tabs li:not(.is-active) a:focus {
      border-bottom-color: transparent;
    }

    .tab-content {
      padding: 1.5rem 0;
    }
    .tabs a {
      background-color: transparent;
      color: $forest;
    }

    .tabs li.is-active a {
      color: $forest;
      background-color: transparent;
      border-bottom: 2px solid $forest;
      border-radius: 0;
      font-weight: 600;
    }

    .b-tabs .tabs li:not(.is-active) a:focus {
      border-bottom-color: transparent;
    }
  }

  .b-skeleton-item {
    margin-bottom: 0.75rem !important;
    border-radius: $radius-large;
  }
  .carousel-skelton-mobile .b-skeleton-item {
    width: auto;
  }
  .vue-map {
    border-radius: $radius-large;
  }
  .tag:not(body).tag--purple {
    border-radius: $radius-rounded;
    color: $white;
    background-color: $forest-2;
  }
  .tag:not(body).is-primary.is-light {
    color: $forest;
  }
  .b-image-wrapper > img {
    object-fit: cover;
  }
  .hero .image {
    img,
    .video {
      display: block;
      height: 22vh;
      border-radius: $radius-large;
      width: 100%;
      object-fit: cover;
    }
  }
  .carousel .carousel-items {
    margin: 0px;
  }

  .columns {
    align-items: start;
  }
  .row {
    display: inline-flex;
  }
  .dropdown-content {
    background-color: white;
    border-radius: 4px;
    -webkit-box-shadow: none;
    box-shadow: none;
    padding-bottom: 0;
    padding-top: 0;
  }
  .row.footer-mobile {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  footer.modal-card-foot {
    background-color: white !important;
  }
}
s {
  text-decoration: none;
  position: relative;
}

s::before {
  top: 50%;
  background: red;
  opacity: 0.7;
  content: "";
  width: 110%;
  position: absolute;
  height: 0.1em;
  border-radius: 0.1em;
  left: -5%;
  white-space: nowrap;
  display: block;
  transform: rotate(-15deg);
}

.collapse-trigger {
  padding: 12px 0px !important;
}

.collapse .collapse-trigger .icon {
  transform: translateY(4px);
}

.collapse-content {
  margin-top: 12px;
}

.message p {
  color: $forest;
}

.message-body {
  color: $forest;
}

.page-footer {
  bottom: -1px;
  z-index: 30;
  background-color: $white;
  border-top: 1px solid $line;
  width: 100%;
  position: fixed;
  left: 0;
  padding: 12px 20px;
  backdrop-filter: blur(5px);
  .price {
    font-weight: 800;
    font-size: 1.25rem;
    color: $forest;
  }
  .columns {
    align-items: center;
    padding: 0.5rem 0;
    margin-right: 0.5rem;
  }
  .label {
    line-height: 17px;
    color: $ink-sub;
    font-weight: 400;
    padding-bottom: 0;
    font-size: 0.8rem;
  }
  .label:not(:last-child) {
    margin-bottom: 0;
  }
  .button {
    height: 3.25rem;
    border-radius: $radius-rounded;
  }
}

.media.review {
  margin: 42px 0px 24px 0px;
}

.no-reviews {
  text-align: center;
  margin: 72px 0px;
}

.review .rate {
  margin: 8px 0px 11px 0px;
}

.card {
  border-radius: $radius-large;
  padding: 32px;
  margin: 16px;
}

@media (min-width: 1024px) {
  .rounded-image {
    height: 468px;
  }
}

::v-deep {
  @media (max-width: 1024px) {
    .carousel .carousel-items {
      width: 100vw;
    }
  }
}

.dropdown-item,
.dropdown .dropdown-menu .has-link a {
  margin: 0;
}

.info--container {
  background-color: $soft;
  padding: 22px;
  border-radius: $radius-large;
}

.card .label {
  color: $ink-sub;
  display: block;
  font-size: 1rem;
  font-weight: normal;
}

.big-img {
  height: 32rem;
  width: -webkit-fill-available;
  border-radius: $radius-large;
}

.small-img {
  height: 10rem;
  margin-bottom: 1rem;
  width: -webkit-fill-available;
  border-radius: $radius-large;
}

.level-item {
  align-items: end;
  justify-content: flex-end;
}

.navigate-back {
  margin-bottom: 32px;
  width: fit-content;
  cursor: pointer;
}

.margin {
  margin: 42px 0px;
}

.label {
  color: $ink-sub;
  font-weight: 500;
  padding-bottom: 12px;
}

.card .title {
  padding-bottom: 32px;
}

.book-column {
  height: 100vh;
  display: flex;
}

.card-content.itinerary {
  margin-top: 1rem;
}

.grey-container {
  padding: 12px;
  display: flex;
  border-radius: $radius-rounded;
  background-color: $soft;
}

.itenerary-options {
  font-size: 0.9rem;
  font-weight: 500;
}

.icon-sm {
  height: 0.9rem;
  width: 0.9rem;
}

.itinerary {
  .item-title {
    font-size: 1.05rem;
  }
}

.rb-container ul.rb li:last-child {
  padding-bottom: 0.5rem;
}

.rb-container {
  margin: 24px;
  display: block;
  position: relative;
}

.rb-container ul.rb {
  margin: 0;
  padding: 0;
  display: inline-block;
}

.rb-container ul.rb li {
  list-style: none;
  margin: auto;
  margin-left: 0;
  min-height: 50px;
  border-left: 3px dotted $line;
  padding: 0 0 2rem 30px;
  position: relative;
}

.rb-container ul.rb li:last-child {
  border-left: 0;
  &::before {
    left: -9px;
  }
}

.rb-container ul.rb li::before {
  position: absolute;
  left: -14px;
  top: 0;
  content: " ";
  border: 9px solid $forest-2;
  border-radius: 500%;
  background: $white;
  height: 24px;
  width: 24px;
  transition: all 500ms ease-in-out;
}

.rb-container ul.rb li:hover::before {
  border-color: $forest;
  transition: all 1000ms ease-in-out;
}

.item-title {
  font-size: 1.0625rem;
  font-weight: 700;
  color: $forest;
}

.item-subtitle {
  color: $ink-sub;
  font-size: 0.9375rem;
  line-height: 1.5;
  font-weight: 400;
  white-space: pre-line;
}

::v-deep {
  .tag:not(body) {
    padding: 1rem;
  }
}
</style>
